import { RelevanceJobStatus } from "@/api";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ScheduleIcon from "@mui/icons-material/Schedule";
import {
    Box,
    CircularProgress,
    Popover,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { alpha, keyframes, styled } from "@mui/material/styles";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useId, useState } from "react";

// ============================================================================
// Types & Constants
// ============================================================================

interface IProps {
    status: RelevanceJobStatus;
    isLoading?: boolean;
    errorMessage?: string;
}

interface StatusConfig {
    icon: React.ReactNode;
    label: string;
    description: string;
    color: string;
    bgColor: string;
    borderColor: string;
}

// ============================================================================
// Animations
// ============================================================================

// Subtle pulse animation for processing state
const pulseKeyframes = keyframes`
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.85;
        transform: scale(1.02);
    }
`;

// Gentle glow ring for processing
const glowKeyframes = keyframes`
    0%, 100% {
        box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.4);
    }
    50% {
        box-shadow: 0 0 0 4px rgba(24, 144, 255, 0.1);
    }
`;

// ============================================================================
// Styled Components
// ============================================================================

const IndicatorContainer = styled(motion.div)<{
    $isProcessing?: boolean;
    $bgColor: string;
    $borderColor: string;
}>(({ theme, $isProcessing, $bgColor, $borderColor }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 10px",
    borderRadius: 20,
    backgroundColor: $bgColor,
    border: `1px solid ${$borderColor}`,
    cursor: "pointer",
    transition: theme.transitions.create(
        ["background-color", "border-color", "box-shadow"],
        { duration: 150 }
    ),
    ...($isProcessing && {
        animation: `${glowKeyframes} 2s ease-in-out infinite`,
    }),
    "&:hover": {
        backgroundColor: alpha($borderColor, 0.15),
    },
    "&:focus-visible": {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
    },
    // Touch target minimum 44px
    minHeight: 32,
    minWidth: 32,
    [theme.breakpoints.down("sm")]: {
        minHeight: 44,
        minWidth: 44,
        padding: "10px 14px",
    },
}));

const IconWrapper = styled(motion.div)<{ $isProcessing?: boolean }>(
    ({ $isProcessing }) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...($isProcessing && {
            animation: `${pulseKeyframes} 2s ease-in-out infinite`,
        }),
    })
);

const StatusLabel = styled(Typography)(({ theme }) => ({
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    marginLeft: 6,
    [theme.breakpoints.down("sm")]: {
        fontSize: "0.75rem",
        marginLeft: 8,
    },
}));

const PopoverContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    maxWidth: 280,
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2.5),
        maxWidth: "90vw",
    },
}));

const PopoverTitle = styled(Typography)(({ theme }) => ({
    fontSize: "0.875rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
}));

const PopoverDescription = styled(Typography)(({ theme }) => ({
    fontSize: "0.8125rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
}));

// Custom styled tooltip for desktop
const StyledTooltip = styled(
    ({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
        <Tooltip {...props} classes={{ popper: className }} />
    )
)(({ theme }) => ({
    "& .MuiTooltip-tooltip": {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[3],
        borderRadius: 8,
        padding: 0,
        maxWidth: 260,
        border: `1px solid ${theme.palette.divider}`,
    },
    "& .MuiTooltip-arrow": {
        color: theme.palette.background.paper,
        "&::before": {
            border: `1px solid ${theme.palette.divider}`,
        },
    },
}));

const TooltipContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
}));

const TooltipHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
}));

const TooltipTitle = styled(Typography)({
    fontSize: "0.8125rem",
    fontWeight: 600,
});

const TooltipDescription = styled(Typography)(({ theme }) => ({
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
}));

// Loading skeleton
const LoadingSkeleton = styled(motion.div)(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "6px 10px",
    borderRadius: 20,
    backgroundColor: alpha(theme.palette.action.hover, 0.5),
    border: `1px solid ${theme.palette.divider}`,
    minHeight: 32,
    minWidth: 60,
    [theme.breakpoints.down("sm")]: {
        minHeight: 44,
        minWidth: 80,
        padding: "10px 14px",
    },
}));

// ============================================================================
// Status Configurations
// ============================================================================

const useStatusConfig = (): Record<RelevanceJobStatus, StatusConfig> => {
    const theme = useTheme();

    return {
        [RelevanceJobStatus.NONE]: {
            icon: <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />,
            label: "Not Scored",
            description:
                "Relevance scoring has not been run for this checklist yet. Update the description to get started.",
            color: theme.palette.grey[600],
            bgColor: alpha(theme.palette.grey[500], 0.12),
            borderColor: alpha(theme.palette.grey[500], 0.32),
        },
        [RelevanceJobStatus.PENDING]: {
            icon: <ScheduleIcon sx={{ fontSize: 16 }} />,
            label: "Queued",
            description: "Relevance scoring is queued and will begin shortly.",
            color: theme.palette.info.main,
            bgColor: alpha(theme.palette.info.main, 0.08),
            borderColor: alpha(theme.palette.info.main, 0.24),
        },
        [RelevanceJobStatus.PROCESSING]: {
            icon: <CircularProgress size={14} thickness={4} />,
            label: "Analyzing",
            description:
                "AI is analyzing clause, topics, and fields relevance. This typically takes a few moments.",
            color: theme.palette.info.main,
            bgColor: alpha(theme.palette.info.main, 0.08),
            borderColor: alpha(theme.palette.info.main, 0.24),
        },
        [RelevanceJobStatus.COMPLETED]: {
            icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
            label: "Complete",
            description:
                "Relevance analysis is complete. Scores are now available.",
            color: theme.palette.success.main,
            bgColor: alpha(theme.palette.success.main, 0.08),
            borderColor: alpha(theme.palette.success.main, 0.24),
        },
        [RelevanceJobStatus.FAILED]: {
            icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
            label: "Failed",
            description: "Relevance analysis encountered an error.",
            color: theme.palette.error.main,
            bgColor: alpha(theme.palette.error.main, 0.08),
            borderColor: alpha(theme.palette.error.main, 0.24),
        },
    };
};

// ============================================================================
// Framer Motion Variants
// ============================================================================

const containerVariants = {
    initial: { opacity: 0 },
    animate: {
        opacity: 1,
        transition: { duration: 0.2 },
    },
    exit: {
        opacity: 0,
        transition: { duration: 0.15 },
    },
};

const iconVariants = {
    initial: { opacity: 0 },
    animate: {
        opacity: 1,
        transition: { duration: 0.15 },
    },
};

// ============================================================================
// Component
// ============================================================================

export const RelevanceStatusIndicator = ({
    status,
    isLoading,
    errorMessage,
}: IProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const prefersReducedMotion = useMediaQuery(
        "(prefers-reduced-motion: reduce)"
    );
    const statusConfig = useStatusConfig();

    // Popover state for mobile
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const popoverId = useId();

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (isMobile) {
                setAnchorEl(event.currentTarget);
            }
        },
        [isMobile]
    );

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const isPopoverOpen = Boolean(anchorEl);

    // Loading state
    if (isLoading) {
        return (
            <LoadingSkeleton
                initial={prefersReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                aria-label="Loading status"
            >
                <CircularProgress
                    size={14}
                    sx={{ color: theme.palette.action.disabled }}
                />
            </LoadingSkeleton>
        );
    }

    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;

    const isProcessing = status === RelevanceJobStatus.PROCESSING;
    const description = errorMessage
        ? `${config.description} ${errorMessage}`
        : config.description;

    // The indicator element (shared between desktop and mobile)
    const indicatorElement = (
        <AnimatePresence mode="wait">
            <IndicatorContainer
                key={status}
                $isProcessing={isProcessing && !prefersReducedMotion}
                $bgColor={config.bgColor}
                $borderColor={config.borderColor}
                variants={prefersReducedMotion ? undefined : containerVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                onClick={handleClick}
                tabIndex={0}
                role="button"
                aria-describedby={
                    isMobile && isPopoverOpen ? popoverId : undefined
                }
                aria-label={`Relevance status: ${config.label}. ${description}`}
            >
                <IconWrapper
                    $isProcessing={isProcessing && !prefersReducedMotion}
                    variants={prefersReducedMotion ? undefined : iconVariants}
                    style={{ color: config.color }}
                >
                    {config.icon}
                </IconWrapper>
                <StatusLabel style={{ color: config.color }}>
                    {config.label}
                </StatusLabel>
            </IndicatorContainer>
        </AnimatePresence>
    );

    // Mobile: Use Popover for tap-to-reveal
    if (isMobile) {
        return (
            <>
                {indicatorElement}
                <Popover
                    id={popoverId}
                    open={isPopoverOpen}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 2,
                                boxShadow: theme.shadows[3],
                                mt: 1,
                            },
                        },
                    }}
                >
                    <PopoverContent>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <Box sx={{ color: config.color, display: "flex" }}>
                                {status === RelevanceJobStatus.PROCESSING ? (
                                    <CircularProgress size={18} thickness={4} />
                                ) : (
                                    config.icon
                                )}
                            </Box>
                            <PopoverTitle style={{ color: config.color }}>
                                {config.label}
                            </PopoverTitle>
                        </Box>
                        <PopoverDescription>{description}</PopoverDescription>
                    </PopoverContent>
                </Popover>
            </>
        );
    }

    // Desktop: Use styled Tooltip for hover
    // Wrap in span to ensure Tooltip can attach refs properly
    return (
        <StyledTooltip
            title={
                <TooltipContent>
                    <TooltipHeader>
                        <Box sx={{ color: config.color, display: "flex" }}>
                            {status === RelevanceJobStatus.PROCESSING ? (
                                <CircularProgress size={14} thickness={4} />
                            ) : (
                                config.icon
                            )}
                        </Box>
                        <TooltipTitle style={{ color: config.color }}>
                            Relevance {config.label}
                        </TooltipTitle>
                    </TooltipHeader>
                    <TooltipDescription>{description}</TooltipDescription>
                </TooltipContent>
            }
            arrow
            placement="bottom"
            enterDelay={200}
            leaveDelay={100}
        >
            <span style={{ display: "inline-flex" }}>{indicatorElement}</span>
        </StyledTooltip>
    );
};
